import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useDeferredValue,
  type UIEvent,
  useCallback,
} from "react";
import { UseInfiniteComboboxProps, type ComboboxProps } from "./ComboboxTypes";

const SCROLL_THRESHOLD = 0.64; // arbitrary number

/**
 * Hook for implementing infinite scroll functionality in combobox components.
 *
 * Features:
 * - Automatic pagination with scroll-based loading
 * - Configurable query triggers (input change, selection change, scroll)
 * - Debounced query execution to prevent excessive API calls
 * - Automatic retry logic with exponential backoff
 * - Support for both single and multiple selection modes
 * - Customizable scroll threshold for triggering new queries
 * - Loading state management with deferred updates
 * - Automatic detection of pagination end
 * - Support for initial items or query-based initialization
 *
 * @param options Configuration options for the infinite combobox
 * @returns Object containing comboboxProps and contentProps for use with Combobox component
 *
 * @example
 * const { comboboxProps, contentProps } = useInfiniteCombobox({
 *   initialItems: [],
 *   initialPage: 0,
 *   initialLoading: true,
 *   query: async ({ page, inputValue, selectedItems }) => {
 *     return await fetchItems({
 *       page,
 *       pageSize: 25,
 *       searchTerm: inputValue,
 *       excludeIds: selectedItems.map(item => item.id),
 *     });
 *   },
 * });
 *
 * return (
 *   <Combobox {...comboboxProps}>
 *     <Combobox.SearchField label="Search items" />
 *     <Combobox.Content {...contentProps}>
 *       {({ items }) => (
 *         <Combobox.List>
 *           {items.map((item, i) => (
 *             <Combobox.Item key={item.id} item={item} index={i}>
 *               {item.name}
 *             </Combobox.Item>
 *           ))}
 *         </Combobox.List>
 *       )}
 *     </Combobox.Content>
 *   </Combobox>
 * );
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useInfiniteCombobox<Item = any>({
  query,
  queryInitialItems,
  initialItems = [],
  initialPage = 0,
  initialLoading = false,
  initialInputValue,
  defaultInputValue,
  initialSelectedItem,
  defaultSelectedItem,
  initialSelectedItems,
  defaultSelectedItems,
  updateOnInputValueChange = true,
  updateOnSelectedItemChange = true,
  updateOnSelectedItemsChange = true,
  queryOnFirstRender = false,
  shouldTriggerQuery: shouldTriggerQueryProp,
}: UseInfiniteComboboxProps<Item>) {
  const [items, setItems] = useState(initialItems);
  const deferredItems = useDeferredValue(items);
  const [page, setPage] = useState(initialPage);
  const [loading, setLoading] = useState(initialLoading);
  const deferredLoading = useDeferredValue(loading);
  const [inputValue, setInputValue] = useState(
    initialInputValue ?? defaultInputValue ?? "",
  );
  const [selectedItem, setSelectedItem] = useState<Item | null>(
    initialSelectedItem ?? defaultSelectedItem ?? null,
  );
  const [selectedItems, setSelectedItems] = useState<Item[]>(
    initialSelectedItems ?? defaultSelectedItems ?? [],
  );

  const scrollerRef = useRef<HTMLDivElement>(null);

  const defaultShouldTriggerQuery = (element: EventTarget & HTMLElement) => {
    const totalScrollHeight = element.scrollHeight - element.clientHeight;
    return element.scrollTop / totalScrollHeight > SCROLL_THRESHOLD;
  };

  // using both state and ref here since the state will be "behind" the ref
  // if another query has been triggered while the current one is in flight
  const [triggerQueryCount, setTriggerQueryCount] = useState(0);
  const triggerQueryCountRef = useRef(0);
  const triggerQuery = () => {
    setTriggerQueryCount((x) => x + 1);
    triggerQueryCountRef.current += 1;
  };

  const [done, setDone] = useState(false);

  const firstRenderRef = useRef(true);
  const scrollTimeoutRef = useRef(false);

  // using a similar state/ref idea for managing loading as well, since we
  // need to know immediately if queries are in flight, but state won't be
  // updated in the onScroll handler fast enough
  const loadingRef = useRef(false);
  const setLoadingStateAndRef = (isLoading: boolean) => {
    setLoading(isLoading);
    loadingRef.current = isLoading;
  };

  const queryRetryCountRef = useRef(0);

  useEffect(() => {
    if (firstRenderRef.current && queryInitialItems) {
      setItems(queryInitialItems());
    }

    // don't run on initial render unless we explicitly choose to
    if (firstRenderRef.current && !queryOnFirstRender) {
      firstRenderRef.current = false;
      return;
    }

    setLoadingStateAndRef(true);

    // using a short debounce delay to batch updates
    const timeoutID = setTimeout(() => {
      // if this query is outdated, don't call it
      if (triggerQueryCount < triggerQueryCountRef.current) return;

      query({
        page,
        inputValue,
        selectedItem,
        selectedItems,
      })
        .then((res) => {
          queryRetryCountRef.current = 0;

          // if this query is outdated, throw away it's results
          if (triggerQueryCount < triggerQueryCountRef.current) return;

          setLoadingStateAndRef(false);
          setPage((x) => x + 1);

          // if the result is nullish or empty
          // assume we've reached the end of the pagination
          if (res == null || (page > 0 && res.length === 0)) {
            setDone(true);
          } else if (page === 0) {
            setItems(() => [...res]);
          } else {
            setItems((prevItems) => [...prevItems, ...res]);
          }
        })
        .catch(() => {
          // if we fail, retry up to 8 times with exponential back-off
          queryRetryCountRef.current += 1;

          if (queryRetryCountRef.current > 7) {
            queryRetryCountRef.current = 0;
            return;
          } else {
            setTimeout(
              () => {
                triggerQuery();
              },
              2 ** queryRetryCountRef.current * 160,
            );
          }
        });
    }, 160);

    return () => {
      clearTimeout(timeoutID);
    };

    // only re-query when we manually call triggerQuery
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerQueryCount]);

  // if the popover isn't scrollable, keep firing more queries
  useLayoutEffect(() => {
    setTimeout(() => {
      if (
        scrollerRef.current &&
        scrollerRef.current.scrollHeight > 0 &&
        scrollerRef.current.clientHeight > 0 &&
        scrollerRef.current.scrollHeight <= scrollerRef.current.clientHeight
      ) {
        triggerQuery();
      }
    }, 160);
  }, [items]);

  // making this separate variable to satisfy react hooks deps linter
  const isEmpty = items.length === 0 && page === 0;

  const onStateChange = useCallback(
    (changes: Parameters<NonNullable<ComboboxProps["onStateChange"]>>[0]) => {
      // if the user interacts and we're empty, fire the first query
      if (isEmpty && "isOpen" in changes && changes.isOpen === true) {
        triggerQuery();
      }

      // otherwise, if inputValue or selectedItems changes, assume we need an
      // entirely new list back from the query
      const inputValueShouldUpdate =
        updateOnInputValueChange && "inputValue" in changes;
      const selectedItemsShouldUpdate =
        updateOnSelectedItemsChange &&
        "selectedItems" in changes &&
        changes.selectedItems;
      const selectedItemShouldUpdate =
        updateOnSelectedItemChange &&
        "selectedItem" in changes &&
        changes.selectedItem !== undefined;

      if (inputValueShouldUpdate) {
        setInputValue(changes.inputValue ?? "");
      }

      if (selectedItemsShouldUpdate) {
        setSelectedItems(changes.selectedItems ?? []);
      }

      if (selectedItemShouldUpdate) {
        setSelectedItem(changes.selectedItem);
      }

      if (
        inputValueShouldUpdate ||
        selectedItemsShouldUpdate ||
        selectedItemShouldUpdate
      ) {
        setPage(0);
        setDone(false);
        triggerQuery();
      }
    },
    [
      isEmpty,
      updateOnInputValueChange,
      updateOnSelectedItemsChange,
      updateOnSelectedItemChange,
    ],
  );

  const onScroll = useCallback(
    (e: UIEvent<HTMLElement>) => {
      // React events don't persist, so we need to store the element through the timeout
      const element = e.currentTarget;

      // using timeoutRef to throttle scroll calls
      // using loadingRef to not make additional queries if a query is already in flight
      if (!scrollTimeoutRef.current && !loadingRef.current) {
        setTimeout(() => {
          const shouldTriggerQuery =
            shouldTriggerQueryProp ?? defaultShouldTriggerQuery;

          if (shouldTriggerQuery(element) && !done) {
            triggerQuery();
          }

          scrollTimeoutRef.current = false;
        }, 40);

        scrollTimeoutRef.current = true;
      }
    },
    [done, shouldTriggerQueryProp],
  );

  return {
    comboboxProps: {
      items: deferredItems,
      loading: deferredLoading,
      initialInputValue,
      defaultInputValue,
      initialSelectedItem,
      defaultSelectedItem,
      initialSelectedItems,
      defaultSelectedItems,
      disableFilter: true, // assume the query will return a filtered/sorted list
      onStateChange,
    },
    contentProps: {
      onScroll,
      scrollerRef,
    },
  };
}
